import {
	Box,
	Divider,
	Grid2,
	Paper,
	Skeleton,
	Typography,
} from "@mui/material";
import { KPICard, KPIThresholds } from "@/shared/components/KPICard";
import { OverviewShiftMetrics } from "../models/types";

type Props = {
	title: string;
	metrics: OverviewShiftMetrics | null;
	loading?: boolean;
};

const KPICardSkeleton = () => (
	<Paper
		elevation={0}
		sx={{
			p: 0.75,
			height: "100%",
			border: "1px solid #ddd",
			borderRadius: 1,
			backgroundColor: "#fff",
			display: "flex",
			flexDirection: "column",
		}}
	>
		<Skeleton variant="text" width={120} height={20} sx={{ mb: 0.4 }} />
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				flex: 1,
				minHeight: 110,
			}}
		>
			<Box sx={{ flex: "1 1 55%", display: "flex", justifyContent: "center" }}>
				<Skeleton variant="circular" width={100} height={100} />
			</Box>
			<Divider orientation="vertical" flexItem />
			<Box
				sx={{
					flex: "1 1 45%",
					display: "flex",
					flexDirection: "column",
					gap: 0.7,
				}}
			>
				<Skeleton variant="text" />
				<Skeleton variant="text" />
			</Box>
		</Box>
	</Paper>
);

const UPTIME_PCT_THRESHOLDS: KPIThresholds = {
	red: 30,
	orange: 45,
	yellow: 60,
	greenSoft: 70,
	greenNormal: 80,
	greenHard: 90,
};

const PRODUCTIVITY_PER_UPTIME_THRESHOLDS: KPIThresholds = {
	red: 60,
	orange: 75,
	yellow: 85,
	greenSoft: 92,
	greenNormal: 95,
	greenHard: 97,
};

const OVERALL_PRODUCTIVITY_THRESHOLDS: KPIThresholds = {
	red: 35,
	orange: 50,
	yellow: 60,
	greenSoft: 70,
	greenNormal: 80,
	greenHard: 90,
};

const fmt = (
	value: number | null | undefined,
	decimals: number,
	unit?: string,
): string => {
	if (value === null || value === undefined || Number.isNaN(value)) return "-";
	const formatted = parseFloat(value.toFixed(decimals));
	return unit ? `${formatted} ${unit}` : `${formatted}`;
};

export const ShiftKPISection = ({ title, metrics, loading }: Props) => {
	if (loading) {
		return (
			<Box sx={{ display: "flex", gap: 1, width: "100%", height: "100%" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						minWidth: "20px",
						maxWidth: "20px",
					}}
				>
					<Typography
						variant="subtitle2"
						sx={{
							fontWeight: 600,
							transform: "rotate(-90deg)",
							whiteSpace: "nowrap",
						}}
					>
						{title}
					</Typography>
				</Box>
				<Grid2 container spacing={1} sx={{ flex: 1, alignItems: "stretch" }}>
					{Array.from({ length: 3 }).map((_, i) => (
						<Grid2 key={i} size={{ xs: 12, md: 4 }}>
							<KPICardSkeleton />
						</Grid2>
					))}
				</Grid2>
			</Box>
		);
	}
	const uptimeHrs = metrics?.UptimeHrs ?? null;
	const uptimePct = metrics?.UptimePct ?? null;
	const productivityPerUptimePct = metrics?.ProductivityPerUptimePct ?? null;
	const overallProductivityPct = metrics?.OverallProductivityPct ?? null;

	return (
		<Box sx={{ display: "flex", gap: 1, width: "100%", height: "100%" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minWidth: "20px",
					maxWidth: "20px",
				}}
			>
				<Typography
					variant="subtitle2"
					sx={{
						fontWeight: 600,
						transform: "rotate(-90deg)",
						whiteSpace: "nowrap",
					}}
				>
					{title}
				</Typography>
			</Box>
			<Grid2 container spacing={1} sx={{ flex: 1, alignItems: "stretch" }}>
				<Grid2 size={{ xs: 12, md: 4 }}>
					<KPICard
						title="Uptime"
						value={uptimeHrs}
						unit="Hrs."
						percentage={uptimePct}
						subtitle="Line in Auto"
						tooltip="Hours running in Auto mode / Shift hours"
						thresholds={UPTIME_PCT_THRESHOLDS}
						details={[
							{
								label: "Auto",
								value: fmt(metrics?.UptimeHrs, 2, "Hrs"),
							},
							{
								label: "Shift",
								value: fmt(metrics?.ShiftHrs, 2, "Hrs"),
							},
						]}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 4 }}>
					<KPICard
						title="Productivity Per Uptime"
						value={productivityPerUptimePct}
						unit="%"
						percentage={productivityPerUptimePct}
						subtitle="Runtime / Uptime"
						tooltip="Bundle runtime / Uptime hours"
						thresholds={PRODUCTIVITY_PER_UPTIME_THRESHOLDS}
						details={[
							{
								label: "Runtime",
								value: fmt(metrics?.RuntimeHrs, 2, "Hrs"),
							},
							{
								label: "Uptime",
								value: fmt(metrics?.UptimeHrs, 2, "Hrs"),
							},
						]}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 4 }}>
					<KPICard
						title="Overall Productivity"
						value={overallProductivityPct}
						unit="%"
						percentage={overallProductivityPct}
						subtitle="Runtime / Available"
						tooltip="Bundle runtime / (Shift - Delays)"
						thresholds={OVERALL_PRODUCTIVITY_THRESHOLDS}
						details={[
							{
								label: "Runtime",
								value: fmt(metrics?.RuntimeHrs, 2, "Hrs"),
							},
							{
								label: "Available",
								value: fmt(metrics?.AvailableHrs, 2, "Hrs"),
							},
						]}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
